import { useState, useEffect, useMemo } from 'react'
import { Card } from '../ui/Card'
import { Button } from '../ui/Button'
import { supabase } from '../../lib/supabase'
import { Check, X, Clock, User, Mail, Target, Calendar } from 'lucide-react'
import { sanitizeText, validateAdminInput, checkRateLimit, logSecurityEvent } from '../../utils/security'
import { useLanguage } from '../../contexts/LanguageContext'

interface PendingUser {
  id: string
  name: string
  email?: string
  age?: number
  experience_level?: string
  training_goal?: string[] | string
  created_at: string
  admin_notes?: string
  approved?: boolean
  approved_at?: string
  approved_by?: string
  user_type?: 'beta_user' | 'auth_only' // Track if user is from beta_users or auth.users only
  meta?: {
    note?: string
    raw_user_meta_data?: Record<string, any> | null
  }
  profile_completed?: boolean | null
}

interface AuthOnlyRpcUser {
  id: string
  email: string | null
  created_at: string
  raw_user_meta_data: { name?: string } | null
}

export default function AdminPanel() {
  const { t } = useLanguage()
  const [users, setUsers] = useState<PendingUser[]>([])
  const [loading, setLoading] = useState(true)
  const [processingId, setProcessingId] = useState<string | null>(null)
  const [authSyncWarning, setAuthSyncWarning] = useState<string | null>(null)

  const pendingCount = useMemo(
    () => users.filter(user => !user.approved || user.profile_completed === false).length,
    [users]
  )
  const sortedUsers = useMemo(() => {
    return [...users].sort((a, b) => {
      const aPending = !a.approved || a.profile_completed === false ? 0 : 1
      const bPending = !b.approved || b.profile_completed === false ? 0 : 1
      if (aPending !== bPending) return aPending - bPending

      const aDate = new Date(a.created_at).getTime()
      const bDate = new Date(b.created_at).getTime()
      return aDate - bDate
    })
  }, [users])

  // Load pending users
  useEffect(() => {
    loadPendingUsers()
  }, [])

  const loadPendingUsers = async () => {
    try {
      const { data: betaUsers, error: betaError } = await supabase
        .from('beta_users')
        .select('*')
        .order('approved', { ascending: true })
        .order('created_at', { ascending: true })

      if (betaError) {
        console.error('Error loading beta users:', betaError)
        return
      }

      const formattedBetaUsers: PendingUser[] = (betaUsers || []).map(user => ({
        ...user,
        approved: user.approved ?? false,
        profile_completed: user.profile_completed ?? null,
        user_type: 'beta_user' as const
      }))

      // Also surface auth-only users (registered but no beta profile yet)
      const { data: authOnlyRpc, error: authRpcError } = await supabase
        .rpc('get_auth_only_users')

      if (authRpcError) {
        console.error('Error loading auth-only users via RPC:', authRpcError)
        setAuthSyncWarning(t('admin.user.authOnlyRpcMissing'))
      } else {
        setAuthSyncWarning(null)
      }

      const authOnlyResults: AuthOnlyRpcUser[] = Array.isArray(authOnlyRpc)
        ? (authOnlyRpc as AuthOnlyRpcUser[])
        : []

      const authOnlyUsers: PendingUser[] = authOnlyResults.map((authOnlyUser: AuthOnlyRpcUser) => {
        const email = authOnlyUser.email ?? undefined
        const displayName = authOnlyUser.raw_user_meta_data?.name
          || (email ? email.split('@')[0] : undefined)
          || t('admin.user.fallbackName')

        return {
          id: authOnlyUser.id,
          name: displayName,
          email,
          created_at: authOnlyUser.created_at,
          approved: false,
          profile_completed: false,
          user_type: 'auth_only' as const,
          meta: {
            note: 'Auth-only user - beta profile missing',
            raw_user_meta_data: authOnlyUser.raw_user_meta_data
          }
        }
      })

      const allUsers = [...authOnlyUsers, ...formattedBetaUsers]

      setUsers(allUsers)
    } catch (error) {
      console.error('Load error:', error)
    } finally {
      setLoading(false)
    }
  }

  const markEmailConfirmed = async (userId: string) => {
    try {
      const { data, error } = await supabase.rpc('mark_user_email_confirmed', {
        target_user_id: userId
      })

      if (error) {
        console.error('Email confirmation RPC error:', error)
        logSecurityEvent('Admin email confirmation error', { error: error.message, userId })
        return false
      }

      if (data !== true) {
        console.warn('Email confirmation RPC returned falsy result for user', userId)
        return false
      }

      return true
    } catch (rpcError: any) {
      console.error('Email confirmation exception:', rpcError)
      logSecurityEvent('Admin email confirmation exception', { userId, error: rpcError?.message })
      return false
    }
  }

  const approveUser = async (userId: string) => {
    // Security: Rate limiting for admin actions
    if (!checkRateLimit('admin_approve', 20, 60000)) {
      alert(t('admin.rateLimit'))
      logSecurityEvent('Admin rate limit exceeded for approve', { userId })
      return
    }

    // Security: Validate userId format (basic UUID check)
    if (!userId || !/^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(userId)) {
      alert(t('admin.invalidUser'))
      logSecurityEvent('Invalid userId in approval', { userId })
      return
    }

    setProcessingId(userId)
    const targetUser = users.find(u => u.id === userId)
    try {
      if (targetUser?.user_type === 'auth_only') {
        const sanitizedName = targetUser.name ? sanitizeText(targetUser.name) : null
        const { data: insertedUser, error: insertError } = await supabase
          .from('beta_users')
          .insert({
            id: userId,
            email: targetUser.email,
            name: sanitizedName,
            age: null,
            experience_level: null,
            training_goal: null,
            approved: true,
            approved_at: new Date().toISOString(),
            approved_by: sanitizeText('Simon'),
            profile_completed: false,
            created_at: new Date().toISOString()
          })
          .select('*')
          .single()

        if (insertError) {
          console.error('Approval insert error:', insertError)
          alert(t('admin.approveError'))
          logSecurityEvent('Admin approval insert error', { error: insertError.message, userId })
          return
        }

        if (insertedUser) {
          const convertedUser: PendingUser = {
            ...insertedUser,
            user_type: 'beta_user',
            approved: insertedUser.approved ?? true,
            profile_completed: insertedUser.profile_completed ?? false
          }

          setUsers(prev => prev.map(u => (u.id === userId ? convertedUser : u)))

          await markEmailConfirmed(userId)

          if (convertedUser.email) {
            window.dispatchEvent(new CustomEvent('userApproved', { detail: { email: sanitizeText(convertedUser.email) } }))
          }

          alert(t('admin.approveSuccess'))
          logSecurityEvent('Admin approved auth-only user', { userId })
        }

        return
      }

      const { data: updatedUser, error } = await supabase
        .from('beta_users')
        .update({
          approved: true,
          approved_at: new Date().toISOString(),
          approved_by: sanitizeText('Simon') // Security: Sanitize admin name
        })
        .eq('id', userId)
        .select('*')
        .single()

      if (error) {
        console.error('Approval error:', error)
        alert(t('admin.approveError'))
        logSecurityEvent('Admin approval error', { error: error.message, userId })
        return
      }

      if (updatedUser) {
        setUsers(prev => prev
          .map(u => (u.id === userId ? { ...u, ...updatedUser, user_type: u.user_type || 'beta_user' } : u)))
      }

      await markEmailConfirmed(userId)

      alert(t('admin.approveSuccess'))
      logSecurityEvent('Admin approved user', { userId })

      // Trigger refresh of main app if user approved themselves
      // Need to trigger by email since we don't have auth.user.id here
      const approvedUser = updatedUser || targetUser
      if (approvedUser?.email) {
        window.dispatchEvent(new CustomEvent('userApproved', { detail: { email: sanitizeText(approvedUser.email) } }))
      }
    } catch (error) {
      console.error('Approval error:', error)
      logSecurityEvent('Admin approval exception', { error, userId })
    } finally {
      setProcessingId(null)
    }
  }

  const rejectUser = async (userId: string, reason: string) => {
    // Security: Rate limiting for admin actions
    if (!checkRateLimit('admin_reject', 10, 60000)) {
      alert(t('admin.rateLimit'))
      logSecurityEvent('Admin rate limit exceeded for reject', { userId })
      return
    }

    // Security: Validate userId format (basic UUID check)
    if (!userId || !/^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(userId)) {
      alert(t('admin.invalidUser'))
      logSecurityEvent('Invalid userId in rejection', { userId })
      return
    }

    // Security: Validate admin input
    const reasonValidation = validateAdminInput.notes(reason)
    if (!reasonValidation.isValid) {
      alert(reasonValidation.error)
      logSecurityEvent('Invalid admin notes in rejection', { reason: reason.substring(0, 100) })
      return
    }

    setProcessingId(userId)
    try {
      const { data: updatedUser, error } = await supabase
        .from('beta_users')
        .update({
          admin_notes: sanitizeText(reason) // Security: Sanitize admin input
        })
        .eq('id', userId)
        .select('*')
        .single()

      if (error) {
        console.error('Rejection error:', error)
        alert(t('admin.rejectError'))
        logSecurityEvent('Admin rejection error', { error: error.message, userId })
        return
      }

      // Optionally delete user entirely
      const shouldDelete = confirm(t('admin.deletePrompt'))
      if (shouldDelete) {
        await supabase.from('beta_users').delete().eq('id', userId)
        logSecurityEvent('Admin deleted user', { userId })
        setUsers(prev => prev.filter(u => u.id !== userId))
      } else if (updatedUser) {
        setUsers(prev => prev
          .map(u => (u.id === userId ? { ...u, ...updatedUser, user_type: u.user_type || 'beta_user' } : u)))
      }
      alert(t('admin.rejectSuccess'))
      logSecurityEvent('Admin rejected user', { userId, reason: reason.substring(0, 100) })
    } catch (error) {
      console.error('Rejection error:', error)
      logSecurityEvent('Admin rejection exception', { error, userId })
    } finally {
      setProcessingId(null)
    }
  }

  const formatGoals = (goals: string[] | string) => {
    if (Array.isArray(goals)) {
      return goals.map(goal => sanitizeText(goal)).join(', ')
    }
    return sanitizeText(goals)
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('de-DE', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto p-6">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-purple-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-600 dark:text-slate-300">{t('admin.loadingPending')}</p>
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-6xl mx-auto p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-slate-100 flex items-center gap-2">
          <Clock className="w-6 h-6 text-purple-500" />
          {t('admin.title')}
        </h1>
        <div className="text-gray-600 dark:text-slate-300 mt-1 space-y-1 text-sm">
          <p>
            {pendingCount > 0
              ? `${pendingCount} ${t('admin.subtitle')}`
              : t('admin.subtitleCleared')}
          </p>
          <p>
            {t('admin.totalUsersLabel')}: {users.length}
          </p>
        </div>
      </div>

      {authSyncWarning && (
        <Card className="mb-6 border border-yellow-300 dark:border-yellow-800 bg-yellow-50 dark:bg-yellow-900/20 p-4 text-sm text-yellow-800 dark:text-yellow-200">
          {authSyncWarning}
        </Card>
      )}

      {users.length === 0 ? (
        <Card className="p-8 text-center">
          <Check className="w-12 h-12 text-green-500 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-slate-100 mb-2">{t('admin.noRegistrations.title')}</h3>
          <p className="text-gray-600 dark:text-slate-300">{t('admin.noRegistrations.subtitle')}</p>
          <Button
            onClick={loadPendingUsers}
            className="mt-4 bg-purple-500 hover:bg-purple-600"
          >
            {t('admin.refresh')}
          </Button>
        </Card>
      ) : (
        <div className="space-y-4">
          {sortedUsers.map(user => {
            const isPendingApproval = !user.approved
            const isAwaitingProfile = !!user.approved && user.profile_completed === false
            const isAuthOnly = user.user_type === 'auth_only'
            const canModerate = user.user_type === 'beta_user'
            return (
              <Card key={user.id} className="p-6">
                <div className="flex justify-between items-start">
                  <div className="flex-1 space-y-3">
                  {/* User Basic Info */}
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                      <User className="w-5 h-5 text-purple-600" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="font-medium text-gray-900 dark:text-slate-100">{sanitizeText(user.name)}</h3>
                        <span className={(() => {
                          if (isAuthOnly) return 'bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300 text-xs px-2 py-1 rounded'
                          if (isPendingApproval) return 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-400 text-xs px-2 py-1 rounded'
                          if (isAwaitingProfile) return 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 text-xs px-2 py-1 rounded'
                          return 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-400 text-xs px-2 py-1 rounded'
                        })()}>
                          {isAuthOnly
                            ? t('admin.user.statusAuthOnly')
                            : isPendingApproval
                              ? t('admin.user.statusPending')
                              : isAwaitingProfile
                                ? t('admin.user.statusAwaitingProfile')
                                : t('admin.user.statusApproved')}
                        </span>
                      </div>
                      <div className="flex items-center gap-4 text-sm text-gray-600 dark:text-slate-300">
                        {user.email && (
                          <span className="flex items-center gap-1">
                            <Mail className="w-4 h-4" />
                            {sanitizeText(user.email)}
                          </span>
                        )}
                        {user.age && <span>{t('admin.user.age')}: {user.age}</span>}
                        {user.experience_level && <span>{t('admin.user.experience')}: {sanitizeText(user.experience_level)}</span>}
                      </div>
                    </div>
                  </div>

                  {/* Training Goals - only for beta users */}
                  {user.training_goal && (
                    <div className="flex items-start gap-2">
                      <Target className="w-4 h-4 text-gray-400 mt-0.5" />
                      <div>
                        <span className="text-sm font-medium text-gray-700 dark:text-slate-200">{t('admin.user.goals')}:</span>
                        <p className="text-sm text-gray-600 dark:text-slate-300">{formatGoals(user.training_goal)}</p>
                      </div>
                    </div>
                  )}

                  {/* Auth-only note */}
                  {user.user_type === 'auth_only' && (
                    <div className="bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800/40 rounded-md p-3 text-sm text-purple-800 dark:text-purple-200">
                      {t('admin.user.missingBetaProfile')}
                    </div>
                  )}

                  {/* Awaiting profile completion note */}
                  {isAwaitingProfile && (
                    <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800/40 rounded-md p-3 text-sm text-blue-800 dark:text-blue-200">
                      {t('admin.user.awaitingProfileCompletionPrompt')}
                    </div>
                  )}

                  {/* Registration Date */}
                  <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-slate-400">
                    <Calendar className="w-4 h-4" />
                    {t('admin.user.registered')}: {formatDate(user.created_at)}
                  </div>

                  {/* Admin Notes */}
                  {user.admin_notes && (
                    <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800/50 rounded-md p-3">
                      <span className="text-sm font-medium text-yellow-800 dark:text-yellow-400">{t('admin.user.notes')}:</span>
                      <p className="text-sm text-yellow-700 dark:text-yellow-300">{sanitizeText(user.admin_notes)}</p>
                    </div>
                  )}
                </div>

                {/* Action Buttons */}
                <div className="flex gap-2">
                  {isAuthOnly ? (
                    <Button
                      onClick={() => approveUser(user.id)}
                      disabled={processingId === user.id}
                      className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 flex items-center gap-2"
                    >
                      <Check className="w-4 h-4" />
                      {t('admin.approve')}
                    </Button>
                  ) : canModerate ? (
                    isPendingApproval ? (
                      <>
                        <Button
                          onClick={() => approveUser(user.id)}
                          disabled={processingId === user.id}
                          className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 flex items-center gap-2"
                        >
                          <Check className="w-4 h-4" />
                          {t('admin.approve')}
                        </Button>
                        <Button
                          onClick={() => {
                            const reason = prompt(t('admin.rejectReason'))
                            if (reason && reason.trim()) {
                              const sanitizedReason = reason.trim().slice(0, 500)
                              rejectUser(user.id, sanitizedReason)
                            }
                          }}
                          disabled={processingId === user.id}
                          className="bg-red-500 hover:bg-red-600 text-white px-4 py-2 flex items-center gap-2"
                        >
                          <X className="w-4 h-4" />
                          {t('admin.reject')}
                        </Button>
                      </>
                    ) : isAwaitingProfile ? (
                      <span className="text-sm text-blue-700 dark:text-blue-300">
                        {t('admin.user.awaitingProfileCompletion')}
                      </span>
                    ) : (
                      <span className="text-sm text-gray-500 dark:text-slate-400">
                        {t('admin.user.alreadyApproved')}
                      </span>
                    )
                  ) : (
                    <span className="text-sm text-gray-500 dark:text-slate-400">
                      {t('admin.user.alreadyApproved')}
                    </span>
                  )}
                </div>
              </div>
            </Card>
          )
        })}
        </div>
      )}
    </div>
  )
}
